/**
 * کیتِ کوچکِ سایت — قرینه‌ی apps/mobile/src/ui/kit.tsx.
 *
 * فرقِ اصلی با موبایل: اینجا استایل داخلِ کامپوننت نوشته نمی‌شه، فقط یه
 * className داده می‌شه و خودِ CSS (ui/base.css) کارش رو می‌کنه. دلیلش همون
 * قاعده‌ی تمه: تعویضِ تم نباید هیچ کامپوننتی رو دوباره رندر کنه.
 */
import React from 'react';
import { tapped } from '@/core/lib/devlog';
import { t } from '@/core/lib/i18n';
import { useOutsideClose } from './useOutsideClose';
import { EyeIcon } from './icons';

const cx = (...parts: (string | false | undefined)[]) => parts.filter(Boolean).join(' ');

/**
 * ═══ چرا هندلرهای این فایل پیچیده می‌شن ═══
 *
 * قرینه‌ی همین یادداشت در apps/mobile/src/ui/kit.tsx: لاگِ فعالیت
 * (core/lib/devlog) باید «هر کاری که کاربر می‌کنه» رو ثبت کنه، و چون هر
 * دکمه‌ی سایت از همین چند کامپوننت میاد، یه بار پیچیدنشون این‌جا یعنی
 * پوششِ کامل. خاموش که باشه `tapped` همون تابعِ اصلی رو برمی‌گردونه.
 */

type BtnKind = 'fill' | 'outline' | 'ghost';

export function Btn({
  label, onClick, kind = 'fill', block, disabled, type = 'button', testId,
}: {
  label: string;
  onClick?: () => void;
  kind?: BtnKind;
  block?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
  /** فقط برای تست‌های E2E؛ وقتی متنِ دکمه در صفحه یکتا نیست. */
  testId?: string;
}) {
  return (
    <button
      type={type}
      data-testid={testId}
      className={cx('btn', kind !== 'outline' && kind, block && 'block')}
      onClick={tapped(label, onClick)}
      disabled={disabled}
    >
      {label}
    </button>
  );
}

export function Card({ children, className, onClick }: {
  children: React.ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void;
}) {
  return <div className={cx('card', className)} onClick={tapped('card', onClick)}>{children}</div>;
}

export function Field({
  label, value, onChange, type = 'text', placeholder, autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'email' | 'password';
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {type === 'password' ? (
        <PasswordInput value={value} placeholder={placeholder}
          autoComplete={autoComplete} onChange={onChange} />
      ) : (
        <input
          className="input"
          type={type}
          value={value}
          placeholder={placeholder}
          autoComplete={autoComplete}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
}

/**
 * فیلدِ رمز با دکمه‌ی چشم — قرینه‌ی `PasswordInput` در کیتِ موبایل (WHOSUP-363).
 *
 * بدونِ این، کسی که سرِ ثبت‌نام رمز را دوباره تایپ می‌کند نمی‌تواند ببیند
 * بارِ اول چه نوشته، و «رمزها یکی نیستند» نمی‌گوید کدامش اشتباه است.
 */
export function PasswordInput({ value, onChange, placeholder, autoComplete }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
}) {
  const [shown, setShown] = React.useState(false);
  return (
    <span className="pw-wrap">
      <input
        className="input"
        type={shown ? 'text' : 'password'}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
      />
      {/* نامِ دکمه عمداً «password» ندارد: تست‌های E2E فیلد را با
          getByLabel('Password') پیدا می‌کنند که زیررشته را هم می‌گیرد، و
          «show password» آن را دوپهلو می‌کرد. */}
      <button type="button" className="pw-eye"
        aria-label={shown ? 'hide' : 'show'} aria-pressed={shown}
        onClick={tapped(shown ? 'hide password' : 'show password', () => setShown((x) => !x))}>
        <EyeIcon off={shown} />
      </button>
    </span>
  );
}

export function Chip({ label, selected, onClick }: {
  label: string; selected: boolean; onClick: () => void;
}) {
  return (
    <button type="button" className={cx('chip', selected && 'on')}
      onClick={tapped(`chip:${label}`, onClick)}>
      {label}
    </button>
  );
}

export function Note({ children, tone }: {
  children: React.ReactNode; tone?: 'brand' | 'warn' | 'ok';
}) {
  return <p className={cx('note', tone)}>{children}</p>;
}

export function Avatar({ url, name, size = 30, onClick }: {
  url?: string | null; name?: string; size?: number; onClick?: () => void;
}) {
  const style = { width: size, height: size, fontSize: size * 0.42 };
  if (url) {
    // lazy: فهرستِ دوستان/مخاطبین صفحه‌بندی نداره و می‌تونه صدها آواتار
    // داشته باشه — مرورگر فقط اون‌هایی رو می‌گیره که به دیده شدن نزدیکن.
    return (
      <img className="avatar" style={style} src={url} alt=""
        loading="lazy" decoding="async"
        onClick={onClick} role={onClick ? 'button' : undefined} />
    );
  }
  return (
    <span className="avatar avatar-fallback" style={style} onClick={onClick}
      role={onClick ? 'button' : undefined}>
      {(name ?? '?').trim().slice(0, 1).toUpperCase()}
    </span>
  );
}

/**
 * راهنما، پشتِ یک «؟» — قرینه‌ی `Help` در apps/mobile/src/ui/kit.tsx.
 *
 * فرق با موبایل عمدی است: اینجا **نگه داشتنِ موس** هم بازش می‌کند (کارِ CSS
 * است، نه state)، چون روی یک مانیتور همان چیزی است که آدم انتظار دارد. کلیک
 * هم کار می‌کند و همان است که روی صفحه‌ی لمسی و با کیبورد تنها راهِ باز
 * کردنش است.
 *
 * `lines` برای راهنمای چندتکه است (مثلِ «در توضیحات اینها را بنویس»).
 */
export function Help({ text, lines, tone }: { text?: string; lines?: string[]; tone?: 'warn' }) {
  const [open, setOpen] = React.useState(false);
  // «!» برای هشداری که باید در دسترس بماند ولی لازم نیست همیشه جلوی چشم باشد.
  return (
    <span className="help-wrap">
      <button type="button" className={cx('help-btn', tone)} aria-label={t('help')}
        aria-expanded={open}
        onClick={tapped('help', () => setOpen((v) => !v))}
        onBlur={() => setOpen(false)}>
        {tone === 'warn' ? '!' : '?'}
      </button>
      <span className={cx('help-bubble', open && 'on')} role="note">
        {text ? <span>{text}</span> : null}
        {(lines ?? []).map((line) => <span key={line}>· {line}</span>)}
      </span>
    </span>
  );
}

/**
 * فهرستِ کارت‌دارِ «یکی رو انتخاب کن» که بعد از انتخاب جمع می‌شه — قرینه‌ی
 * `PickList` در apps/mobile/src/ui/kit.tsx.
 *
 * فرقش با `Select`: این‌جا گزینه‌ها از اول **باز**ن، چون خودِ گزینه‌ها
 * (عنوان + توضیح) بخشی از فهمیدنِ فرمن — مثل نوعِ سیگنال. بعد از انتخاب،
 * فهرست به همون یه کارتِ انتخاب‌شده جمع می‌شه و کلیک دوباره بازش می‌کنه.
 * `startOpen={false}` برای فرمِ ویرایش، که انتخاب از قبل شده.
 */
export function PickList<T extends string>({ value, items, onChange, startOpen = true }: {
  value: T;
  items: { value: T; label: string; hint?: string }[];
  onChange: (v: T) => void;
  startOpen?: boolean;
}) {
  const [open, setOpen] = React.useState(startOpen);
  const current = items.find((i) => i.value === value);

  if (!open) {
    return (
      <button type="button" className="pick on pick-closed" aria-expanded={false}
        onClick={tapped(`pick:${current?.label ?? ''}`, () => setOpen(true))}>
        <b>{current?.label}</b>
        {current?.hint ? <small>{current.hint}</small> : null}
        <span className="select-chev" aria-hidden>▾</span>
      </button>
    );
  }
  return (
    <div className="picklist">
      {items.map((it) => (
        <button key={it.value} type="button" aria-pressed={it.value === value}
          className={cx('pick', it.value === value && 'on')}
          onClick={tapped(`pick:${it.value}`, () => { onChange(it.value); setOpen(false); })}>
          <b>{it.label}</b>
          {it.hint ? <small>{it.hint}</small> : null}
        </button>
      ))}
    </div>
  );
}

/**
 * انتخابِ یکی از چندتا، جمع‌شده توی یه سطر — قرینه‌ی `Dropdown` در
 * apps/mobile/src/ui/kit.tsx.
 *
 * تا وقتی باز نشده فقط جوابِ فعلی دیده می‌شه؛ کلیک فهرست رو زیرش باز
 * می‌کنه (شناور، مثل بقیه‌ی کادرهای وب — نه درجا مثلِ موبایل که لایه‌ی
 * شناور روی صفحه‌ی کناری دو لایه می‌شد). `hint` خطِ دومِ کم‌رنگِ هر
 * گزینه‌ست، برای گزینه‌هایی که اسمشون به‌تنهایی گویا نیست.
 */
export function Select<T extends string>({ value, items, onChange }: {
  value: T;
  items: { value: T; label: string; hint?: string }[];
  onChange: (v: T) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  useOutsideClose(ref, open, () => setOpen(false));
  const current = items.find((i) => i.value === value);
  return (
    <div className="select" ref={ref}>
      <button type="button" className={cx('select-trigger', open && 'open')}
        aria-haspopup="listbox" aria-expanded={open}
        onClick={tapped(`select:${current?.label ?? ''}`, () => setOpen((x) => !x))}>
        <span>{current?.label ?? ''}</span>
        <span className="select-chev" aria-hidden>▾</span>
      </button>
      {open ? (
        <div className="menu select-menu" role="listbox">
          {items.map((it) => (
            <button key={it.value} type="button" role="option"
              aria-selected={it.value === value}
              className={cx('menu-item', it.value === value && 'active')}
              onClick={tapped(`select:${it.value}`, () => { onChange(it.value); setOpen(false); })}>
              {it.label}
              {it.hint ? <small>{it.hint}</small> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function Checkbox({ label, checked, onChange }: {
  label: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label className="checkbox">
      <input type="checkbox" checked={checked}
        onChange={tapped(`check:${label}`, (e: React.ChangeEvent<HTMLInputElement>) => {
          onChange(e.target.checked);
        })} />
      <span>{label}</span>
    </label>
  );
}
